/**
 * @file combobox.tsx
 * @description Простой Combobox с фильтрацией + inline-create (phase08.5).
 * Использует Popover + Input + список — минимальный набор без base-ui Combobox
 * (последний — более сложный API, не нужен для наших use-case'ов).
 *
 * phase19.31 (SC58, часовой пояс магазина): группы пунктов (`group` у опции —
 * заголовок группы перед первым её пунктом: «Россия», «Другие»), `id` для
 * связи с подписью поля и `invalid`/`describedBy` — ошибка сервера у поля.
 * Повторная приёмка phase19.31: на телефоне список открывается нижней
 * шторкой, значок триггера — шеврон вниз, как у `SelectField`; поиск и
 * пункты — общий `ComboboxList`.
 *
 * Замечания владельца 2026-10-19 (карточка товара, «Производитель»):
 * `clearLabel` — пункт «без значения» первым в списке (`onChange(null)`), чтобы
 * снять уже выбранное значение; фокус в поиск ставит сам список с
 * `preventScroll`, поэтому собственный начальный фокус base-ui выключен
 * (`initialFocus={false}`) — он фокусирует без `preventScroll`, и страница
 * прокручивалась к ещё не спозиционированному окну.
 */

import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import { ComboboxList } from "./combobox-list";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./sheet";
import { PHONE_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";

/**
 * Склеивает ref триггера popover с ref вызывающего: base-ui отдаёт свой ref
 * через props, и молча затереть его нельзя — сломается позиционирование.
 */
function mergeRefs<T>(
  ...refs: (React.Ref<T> | undefined)[]
): (node: T | null) => void {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.RefObject<T | null>).current = node;
    }
  };
}

export interface ComboboxOption {
  value: string;
  label: string;
  /** Дополнительная строка для отображения под label. */
  description?: string;
  disabled?: boolean;
  /** Группа пункта: заголовок выводится перед первым пунктом группы. */
  group?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /**
   * Подпись пункта «без значения» первым в списке («Отсутствует»): вызывает
   * `onChange(null)` и отмечен выбранным при `value === null`. Не задана —
   * пункта нет, снять выбор нельзя (прежнее поведение).
   */
  clearLabel?: string;
  /** Подпись для inline-create-кнопки. Если задано — кнопка появится. */
  createLabel?: string;
  onCreateClick?: () => void;
  disabled?: boolean;
  /**
   * Ref на кнопку-триггер: складские формы переводят в неё фокус после скана
   * артикула. Искать триггер через `querySelector("button")` было нельзя —
   * в момент вызова он ещё `disabled` и в DOM может не быть.
   */
  triggerRef?: React.Ref<HTMLButtonElement>;
  className?: string;
  /** id кнопки-триггера — для `<Label htmlFor>`. */
  id?: string;
  /** Ошибка у поля: красная рамка и `aria-invalid`. */
  invalid?: boolean;
  /** id подсказки или текста ошибки под полем. */
  describedBy?: string;
  /** Подпись для скринридера, если видимой подписи нет. */
  ariaLabel?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Выбрать…",
  searchPlaceholder = "Поиск…",
  emptyText = "Ничего не найдено",
  clearLabel,
  createLabel,
  onCreateClick,
  disabled,
  triggerRef,
  className,
  id,
  invalid,
  describedBy,
  ariaLabel,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  // Телефон: список — нижней шторкой (всплывающее окно у края экрана
  // перекрывалось клавиатурой; phase19.31, часовой пояс магазина).
  const phone = useMediaQuery(PHONE_QUERY);
  const selectedOption = options.find((o) => o.value === value) ?? null;

  function close() {
    setOpen(false);
    setQuery("");
  }

  const list = (
    <ComboboxList
      options={options}
      value={value}
      query={query}
      onQueryChange={setQuery}
      onPick={(next) => {
        onChange(next);
        close();
      }}
      searchPlaceholder={searchPlaceholder}
      emptyText={emptyText}
      clearLabel={clearLabel}
      createLabel={createLabel}
      onCreate={
        onCreateClick
          ? () => {
              close();
              onCreateClick();
            }
          : undefined
      }
      touch={phone}
    />
  );

  /** Кнопка-триггер — одна на оба способа показа. */
  const trigger = (props: React.ComponentPropsWithRef<"button">) => (
    <Button
      {...props}
      ref={mergeRefs(props.ref, triggerRef)}
      id={id}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      variant="outline"
      type="button"
      disabled={disabled}
      className={cn(
        "w-full justify-between font-normal",
        !selectedOption && "text-on-surface-muted",
        invalid && "[box-shadow:inset_0_0_0_1px_var(--destructive)]",
        className,
      )}
    >
      <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
      {/* Тот же значок, что у `SelectField` (шеврон вниз): два выпадающих
          поля одной формы не должны выглядеть по-разному (дефект приёмки
          2026-10-02, «Настройки → Магазин»). */}
      <ChevronDown className="size-4 shrink-0 opacity-50" />
    </Button>
  );

  if (phone) {
    return (
      <>
        {trigger({ onClick: () => setOpen(true), "aria-haspopup": "dialog" })}
        <Sheet open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
          <SheetContent side="bottom" initialFocus={false} className="max-h-[80vh] gap-2 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <SheetHeader>
              <SheetTitle className="type-card-title">{placeholder}</SheetTitle>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col">{list}</div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <PopoverTrigger render={trigger} />
      <PopoverContent align="start" initialFocus={false} sideOffset={4} className="flex max-h-72 min-w-[var(--anchor-width)] flex-col overflow-hidden p-0">
        {list}
      </PopoverContent>
    </Popover>
  );
}
